import * as Location from 'expo-location';
import { Alert, Platform } from 'react-native';

export interface LocationCoordinates {
  longitude: number;
  latitude: number;
}

class LocationService {
  private permissionStatus: Location.PermissionStatus | null = null;
  private lastLocation: LocationCoordinates | null = null;
  private lastLocationTimestamp: number = 0;
  private readonly LOCATION_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Request location permissions
   * @returns true if permission granted, false otherwise
   */
  async requestPermission(): Promise<boolean> {
    try {
      // Check if permission is already granted
      const { status: existingStatus } = await Location.getForegroundPermissionsAsync();
      
      if (existingStatus === 'granted') {
        this.permissionStatus = existingStatus;
        return true;
      }

      // Request permission
      const { status } = await Location.requestForegroundPermissionsAsync();
      this.permissionStatus = status;

      if (status !== 'granted') {
        if (Platform.OS === 'ios') {
          Alert.alert(
            'Location Permission Required',
            'Location permission is needed to record where canvassing interactions take place. Please enable it in Settings.',
            [{ text: 'OK' }]
          );
        } else {
          Alert.alert(
            'Location Permission Required',
            'Location permission is needed to record where canvassing interactions take place. Please enable it in app settings.',
            [{ text: 'OK' }]
          );
        }
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error requesting location permission:', error);
      return false;
    }
  }

  /**
   * Check if location permission is granted
   */
  async hasPermission(): Promise<boolean> {
    try {
      const { status } = await Location.getForegroundPermissionsAsync();
      this.permissionStatus = status;
      return status === 'granted';
    } catch (error) {
      console.error('Error checking location permission:', error);
      return false;
    }
  }

  /**
   * Get current location
   * @returns Location coordinates or null if unavailable
   */
  async getCurrentLocation(): Promise<LocationCoordinates | null> {
    try {
      // Check permission first
      const hasPermission = await this.hasPermission();
      if (!hasPermission) {
        // Try to request permission
        const granted = await this.requestPermission();
        if (!granted) {
          return null;
        }
      }

      // Check if we have a recent cached location
      const now = Date.now();
      if (
        this.lastLocation &&
        now - this.lastLocationTimestamp < this.LOCATION_CACHE_DURATION
      ) {
        return this.lastLocation;
      }

      // Get fresh location
      const location = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.Balanced, // Good balance between accuracy and battery
        maximumAge: 60000, // Accept location up to 1 minute old
        timeout: 10000, // 10 second timeout
      });

      if (location && location.coords) {
        const coordinates: LocationCoordinates = {
          longitude: location.coords.longitude,
          latitude: location.coords.latitude,
        };

        // Cache the location
        this.lastLocation = coordinates;
        this.lastLocationTimestamp = now;

        return coordinates;
      }

      return null;
    } catch (error) {
      console.error('Error getting current location:', error);
      // Return cached location if available, even if expired
      return this.lastLocation;
    }
  }

  /**
   * Get location for interaction (formatted as [longitude, latitude] array)
   * @returns [longitude, latitude] array or null if unavailable
   */
  async getInteractionLocation(): Promise<[number, number] | null> {
    const location = await this.getCurrentLocation();
    if (location) {
      return [location.longitude, location.latitude];
    }
    return null;
  }

  /**
   * Clear cached location
   */
  clearCache(): void {
    this.lastLocation = null;
    this.lastLocationTimestamp = 0;
  }
}

// Export singleton instance
export const locationService = new LocationService();
